import { Component, Input, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageService, ConfirmationService } from 'primeng/api';
import { AssayDocumentsService, IAssayDocumentItem } from '@servicios/assay/assay-documents.service';
import { QualityDocsService } from '@servicios/qualityDocs/qualityDocs.service';
import { qualityDocVersionsService } from '@servicios/qualityDocs/qualityDocVersions.service';

interface ScopeOption {
  label: string;
  value: string; // 'ALL' | 'GENERAL' | assayAnaliteId
  isAnalite?: boolean;
}

@Component({
  selector: 'app-assay-documents-tab',
  templateUrl: './assay-documents-tab.component.html',
  styleUrls: ['./assay-documents-tab.component.scss'],
  standalone: false
})
export class AssayDocumentsTabComponent implements OnInit, OnDestroy {
  @Input() assay: any;

  private subs = new Subscription();

  isLoading = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  allDocuments = signal<IAssayDocumentItem[]>([]);
  availableQualityDocs = signal<any[]>([]);
  selectedScope = signal<string>('ALL');

  // Modal para asociar documento
  showLinkModal = false;
  linkForm!: UntypedFormGroup;

  roleOptions = [
    { label: 'Principal', value: 'PRINCIPAL' },
    { label: 'Auxiliar', value: 'AUXILIAR' }
  ];

  scopeOptions = computed<ScopeOption[]>(() => {
    const opts: ScopeOption[] = [
      { label: 'Todos los documentos', value: 'ALL' },
      { label: 'Ensayo General', value: 'GENERAL' }
    ];

    const analites = this.assay?.analites || [];
    analites.forEach((a: any) => {
      const name = a.analite?.name || a.analite?.code || a.importReference || 'Determinación';
      opts.push({
        label: `Determinación: ${name}`,
        value: a.id,
        isAnalite: true
      });
    });

    return opts;
  });

  filteredDocuments = computed(() => {
    const scope = this.selectedScope();
    const docs = this.allDocuments();

    if (scope === 'ALL') {
      return docs;
    }
    if (scope === 'GENERAL') {
      return docs.filter(d => !d.assayAnaliteId);
    }
    return docs.filter(d => d.assayAnaliteId === scope);
  });

  principalDocuments = computed(() => {
    return this.filteredDocuments().filter(d => d.role === 'PRINCIPAL');
  });

  auxiliaryDocuments = computed(() => {
    return this.filteredDocuments().filter(d => d.role === 'AUXILIAR');
  });

  constructor(
    private fb: UntypedFormBuilder,
    private assayDocsService: AssayDocumentsService,
    private qualityDocsService: QualityDocsService,
    private qualityDocVersionsService: qualityDocVersionsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadAssayDocuments();
    this.loadAvailableQualityDocs();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  initForm(): void {
    this.linkForm = this.fb.group({
      targetScope: ['GENERAL', Validators.required],
      qualityDocumentId: [null, Validators.required],
      role: ['PRINCIPAL', Validators.required],
      notes: ['']
    });
  }

  loadAssayDocuments(): void {
    if (!this.assay?.id) return;
    this.isLoading.set(true);

    const query = this.assayDocsService.watchAssayDocuments(this.assay.id);
    this.subs.add(
      query.valueChanges.subscribe({
        next: ({ data }) => {
          this.allDocuments.set(data?.assayDocuments || []);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error('Error al cargar documentos del ensayo:', err);
          this.isLoading.set(false);
        }
      })
    );
  }

  loadAvailableQualityDocs(): void {
    const query = this.qualityDocsService.getAllValid();
    this.subs.add(
      query.valueChanges.subscribe({
        next: ({ data }: any) => {
          this.availableQualityDocs.set(data?.validQualityDocs || []);
        },
        error: (err) => {
          console.error('Error al cargar documentos de calidad vigentes:', err);
        }
      })
    );
  }

  openLinkDialog(preselectedRole?: 'PRINCIPAL' | 'AUXILIAR'): void {
    const defaultScope = this.selectedScope() !== 'ALL' ? this.selectedScope() : 'GENERAL';
    this.linkForm.reset({
      targetScope: defaultScope,
      qualityDocumentId: null,
      role: preselectedRole || 'PRINCIPAL',
      notes: ''
    });
    this.showLinkModal = true;
  }

  saveLink(): void {
    if (this.linkForm.invalid || !this.assay?.id) return;

    this.isSaving.set(true);
    const formVal = this.linkForm.value;

    const isGeneral = formVal.targetScope === 'GENERAL';
    const payload = {
      assayId: this.assay.id,
      assayAnaliteId: isGeneral ? null : formVal.targetScope,
      qualityDocumentId: formVal.qualityDocumentId,
      role: formVal.role,
      notes: formVal.notes || null
    };

    this.assayDocsService.linkAssayDocument(payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Documento Vinculado',
          detail: 'El documento de calidad se asoció exitosamente.'
        });
        this.isSaving.set(false);
        this.showLinkModal = false;
        this.loadAssayDocuments();
      },
      error: (err) => {
        console.error('Error al vincular documento:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo vincular el documento. Verifique que no esté ya asociado con el mismo rol.'
        });
        this.isSaving.set(false);
      }
    });
  }

  confirmUnlink(doc: IAssayDocumentItem): void {
    const docName = doc.qualityDocument?.name || 'este documento';
    this.confirmationService.confirm({
      message: `¿Está seguro de que desea desvincular "${docName}" de este ensayo?`,
      header: 'Confirmar desvinculación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, desvincular',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      rejectButtonStyleClass: 'p-button-secondary p-button-sm p-button-text',
      accept: () => {
        this.unlinkDocument(doc.id);
      }
    });
  }

  private unlinkDocument(id: string): void {
    this.assayDocsService.unlinkAssayDocument(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Documento Desvinculado',
          detail: 'El documento fue quitado del ensayo.'
        });
        this.loadAssayDocuments();
      },
      error: (err) => {
        console.error('Error al desvincular documento:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo desvincular el documento.'
        });
      }
    });
  }

  getValidVersion(qualityDoc: any): any {
    return this.assayDocsService.getValidQualityDocVersion(qualityDoc);
  }

  downloadDocumentFile(qualityDoc: any): void {
    const validVer = this.getValidVersion(qualityDoc);
    if (!validVer?.file) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin archivo adjunto',
        detail: 'La versión vigente no tiene un archivo PDF adjunto.'
      });
      return;
    }

    this.qualityDocVersionsService.downloadFile(validVer.file).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${qualityDoc.code || 'DOC'}_v${validVer.versionNumber}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error al descargar archivo:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error de descarga',
          detail: 'No se pudo descargar el archivo del servidor.'
        });
      }
    });
  }

  getScopeLabel(doc: IAssayDocumentItem): string {
    if (!doc.assayAnaliteId) {
      return 'Ensayo General';
    }
    const analite = doc.assayAnalite?.analite?.name || doc.assayAnalite?.analite?.code || doc.assayAnalite?.importReference;
    return analite ? `Determinación: ${analite}` : 'Determinación';
  }
}
